import ClearIcon from "@/assets/images/icons/clear-icon.svg";
import SearchIcon from "@/assets/images/icons/search-icon.svg";
import { BRAND_COLORS } from "@/lib/brandColors";
import { DASHBOARD_TOOLBAR_CONTROL_HEIGHT } from "@/lib/dashboardToolbarUi";
import React from "react";
import {
  Platform,
  Pressable,
  TextInput,
  type TextInputProps,
} from "react-native";
import { XStack, type XStackProps } from "tamagui";

export const DESKTOP_SEARCH_MAX_WIDTH = 270;

type SearchInputFieldProps = {
  value: string;
  onChangeText: (text: string) => void;
  placeholder: string;
  autoFocus?: boolean;
  onFocus?: TextInputProps["onFocus"];
  onKeyPress?: TextInputProps["onKeyPress"];
  onSubmitEditing?: TextInputProps["onSubmitEditing"];
  isMobile?: boolean;
  maxWidth?: number;
  width?: XStackProps["width"];
  flex?: number;
  trailing?: React.ReactNode;
  showClearButton?: boolean;
  onClear?: () => void;
  containerProps?: Omit<XStackProps, "children">;
};

export function SearchInputField({
  value,
  onChangeText,
  placeholder,
  autoFocus,
  onFocus,
  onKeyPress,
  onSubmitEditing,
  isMobile,
  maxWidth = DESKTOP_SEARCH_MAX_WIDTH,
  width,
  flex,
  trailing,
  showClearButton,
  onClear,
  containerProps,
}: SearchInputFieldProps) {
  const showBuiltInClear = showClearButton && value.length > 0 && !trailing;
  const hasTrailing = Boolean(trailing) || showBuiltInClear;

  const resolvedWidth =
    width ?? (isMobile !== undefined ? (isMobile ? "100%" : maxWidth) : "100%");

  const handleClear = () => {
    if (onClear) onClear();
    else onChangeText("");
  };

  return (
    <XStack
      width={resolvedWidth}
      flex={flex}
      minWidth={0}
      height={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
      minHeight={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
      maxHeight={DASHBOARD_TOOLBAR_CONTROL_HEIGHT}
      ai="center"
      overflow="hidden"
      br={30}
      borderWidth={1}
      borderColor={BRAND_COLORS.border}
      backgroundColor="white"
      pl={isMobile ? 12 : 14}
      pr={hasTrailing ? 8 : isMobile ? 12 : 14}
      gap={8}
      {...containerProps}
    >
      <SearchIcon width={16} height={16} color="#9E9893" />
      <TextInput
        value={value}
        onChangeText={onChangeText}
        placeholder={placeholder}
        placeholderTextColor={BRAND_COLORS.muted}
        autoFocus={autoFocus}
        onFocus={onFocus}
        onKeyPress={onKeyPress}
        onSubmitEditing={onSubmitEditing}
        style={[
          {
            flex: 1,
            minWidth: 0,
            height: "100%",
            fontSize: 14,
            fontWeight: "400",
            color: BRAND_COLORS.text,
            padding: 0,
            margin: 0,
            backgroundColor: "transparent",
            borderWidth: 0,
          },
          Platform.OS === "web" ? ({ outlineStyle: "none" } as object) : null,
        ]}
        textAlignVertical="center"
        {...(Platform.OS === "android" ? { includeFontPadding: false } : {})}
      />
      {trailing}
      {showBuiltInClear ? (
        <Pressable
          onPress={handleClear}
          hitSlop={8}
          style={{ justifyContent: "center", alignItems: "center" }}
        >
          <ClearIcon width={16} />
        </Pressable>
      ) : null}
    </XStack>
  );
}
